import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import classNames from "classnames"

import { useAppDispatch } from "store/hooks"
import { showAccountSettingsModal } from "store/modalSlise"
import { ReactComponent as IconLogout } from "assets/svg/icon-logout.svg"
import { ReactComponent as LockClosed } from "assets/svg/lock-closed.svg"
import { ReactComponent as IconAccountSettings } from "assets/svg/modal-icon-account-settings.svg"
import { ReactComponent as UserAvatar } from "assets/svg/user-avatar.svg"
import { ReactComponent as Users } from "assets/svg/users.svg"

import { checkRoleType } from "utils/checkRoleType"
import { useLogout } from "hooks/useLogout"
import { useUserProfile } from "hooks/useUserProfile"

import styles from "./index.module.scss"

export const UserMenu = () => {
  const dispatch = useAppDispatch()
  const logout = useLogout()
  const navigate = useNavigate()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()
  const { userProfile, userRole, isEnterprise, isContractor } = useUserProfile()
  const isAccountPage = pathname.startsWith("/account")
  const isMasterPassAllowed = [
    "vendor-admin",
    "sales-manager",
    "contractor-admin",
    "contractor-manager",
    "contractor-engineer",
    "contractor-technician",
  ].includes(checkRoleType(userRole) || "")
  const userManagementAllowed = ["contractor-manager", "contractor-admin"].includes(checkRoleType(userRole) || "")
  const [isDropdownVisible, setDropdownVisible] = useState(false)

  const profileName = useMemo(() => {
    if (isEnterprise) {
      return userProfile?.enterprise?.full_name || userProfile?.name || userProfile?.username || "Account"
    }

    if (isContractor) {
      return userProfile?.contractor?.full_name || userProfile?.name || userProfile?.username || "Account"
    }

    return userProfile?.name || userProfile?.username || "Account"
  }, [isEnterprise, isContractor, userProfile])

  const closeDropdown = () => {
    setDropdownVisible(false)
  }

  const openAccountSettings = () => {
    if (isEnterprise) {
      navigate("/account")
      closeDropdown()
      return
    }

    if (isContractor) {
      dispatch(showAccountSettingsModal(true))
      closeDropdown()
    }
  }

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        closeDropdown()
      }
    }

    document.addEventListener("mousedown", closeOnOutsideClick)

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick)
    }
  }, [])

  const canOpenAccountSettings = isEnterprise || userProfile?.type === 4 || userProfile?.type === 9

  return (
    <div ref={wrapperRef} className={styles.userMenuWrapper}>
      <button
        type="button"
        className={classNames(styles.userMenuButton, { [styles.active]: isDropdownVisible })}
        onClick={() => setDropdownVisible(open => !open)}
        aria-expanded={isDropdownVisible}
        aria-haspopup="menu"
        aria-label="Open user menu"
      >
        <UserAvatar className={styles.userAvatar} />
        <span className={styles.triggerLabel}>{profileName}</span>
        <span className={classNames(styles.arrow, { [styles.arrowOpen]: isDropdownVisible })} aria-hidden="true" />
      </button>
      <div className={classNames(styles.userDropdown, { [styles.hidden]: !isDropdownVisible })}>
        {isAccountPage && !isEnterprise && (
          <Link
            to="/account/profile"
            className={classNames(styles.userDropdownItem, styles.accountLink)}
            onClick={closeDropdown}
          >
            <IconAccountSettings className={styles.itemIcon} />
            Account
          </Link>
        )}
        <div
          className={classNames(styles.userDropdownItem, styles.userDropdownItemCurrent, {
            [styles.skipPointer]: isAccountPage || !canOpenAccountSettings,
          })}
          onClick={!isAccountPage && canOpenAccountSettings ? openAccountSettings : undefined}
        >
          <UserAvatar className={classNames(styles.itemIcon, styles.userAvatarDark)} />
          <div className={styles.userMeta}>
            <div className={styles.userName}>{profileName}</div>
            <div className={styles.userEmail}>{userProfile?.email}</div>
          </div>
        </div>
        {!isEnterprise && !isAccountPage && userManagementAllowed && (
          <Link to="/users" className={styles.userDropdownItem} onClick={closeDropdown}>
            <Users className={styles.itemIcon} />
            User Management
          </Link>
        )}
        {!isEnterprise && !isAccountPage && isMasterPassAllowed && (
          <Link to="/master-pass" className={styles.userDropdownItem} onClick={closeDropdown}>
            <LockClosed className={styles.itemIcon} />
            Master Pass
          </Link>
        )}
        <div
          className={styles.userDropdownItem}
          onClick={() => {
            closeDropdown()
            logout()
          }}
          data-testid="user-menu-logout"
        >
          <IconLogout className={styles.itemIcon} />
          Log Out
        </div>
      </div>
    </div>
  )
}
